import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress, 
  Paper,
  Divider,
  Container
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { 
  collection, 
  getDocs, 
  query, 
  updateDoc,
  deleteDoc, 
  doc,
  writeBatch,
  where
} from 'firebase/firestore';

const ResetFriendRequests: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleResetAllRequests = async () => {
    if (!currentUser || !window.confirm('Are you sure you want to reset ALL friend requests in the system? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // 1. Delete all documents in the friendRequests collection
      const requestsRef = collection(db, 'friendRequests');
      const requestsSnapshot = await getDocs(requestsRef);
      
      const requestBatch = writeBatch(db);
      let requestsDeleted = 0;
      
      requestsSnapshot.forEach(docSnapshot => {
        requestBatch.delete(doc(db, 'friendRequests', docSnapshot.id));
        requestsDeleted++;
      });
      
      await requestBatch.commit();

      // 2. Clear friendRequests and sentRequests arrays from all user documents
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const userBatch = writeBatch(db);
      let usersUpdated = 0;
      
      usersSnapshot.forEach(docSnapshot => {
        const userData = docSnapshot.data();
        if (userData.friendRequests?.length > 0 || userData.sentRequests?.length > 0) {
          userBatch.update(doc(db, 'users', docSnapshot.id), {
            friendRequests: [],
            sentRequests: []
          });
          usersUpdated++;
        }
      });
      
      await userBatch.commit();

      setResult({
        success: true,
        message: `Successfully reset friend requests: ${requestsDeleted} request documents deleted and ${usersUpdated} user profiles updated.`
      });
    } catch (error: any) {
      console.error('Error resetting friend requests:', error);
      setResult({
        success: false,
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetCurrentUserRequests = async () => {
    if (!currentUser || !window.confirm('Are you sure you want to reset your friend requests? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // 1. Delete all documents in the friendRequests collection related to the current user
      const requestsRef = collection(db, 'friendRequests');
      const sentQuery = query(requestsRef, where('fromUserId', '==', currentUser.uid));
      const receivedQuery = query(requestsRef, where('toUserId', '==', currentUser.uid));
      
      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(sentQuery),
        getDocs(receivedQuery)
      ]);
      
      const requestBatch = writeBatch(db);
      let requestsDeleted = 0;
      
      sentSnapshot.forEach(docSnapshot => {
        requestBatch.delete(doc(db, 'friendRequests', docSnapshot.id));
        requestsDeleted++;
      });
      
      receivedSnapshot.forEach(docSnapshot => {
        requestBatch.delete(doc(db, 'friendRequests', docSnapshot.id));
        requestsDeleted++;
      });
      
      await requestBatch.commit();

      // 2. Clear friendRequests and sentRequests arrays from current user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        friendRequests: [],
        sentRequests: []
      });

      setResult({
        success: true,
        message: `Successfully reset your friend requests: ${requestsDeleted} request documents deleted and your profile updated.`
      });
    } catch (error: any) {
      console.error('Error resetting friend requests:', error);
      setResult({
        success: false,
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Reset Friend Requests
          </Typography>
          <Alert severity="warning">
            You must be logged in to use this tool.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Reset Friend Requests
        </Typography>
        <Typography variant="body1" paragraph>
          Use this tool to reset friend requests for testing purposes.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResetCurrentUserRequests}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset My Friend Requests'}
          </Button>
          <Typography variant="caption" display="block" mt={1}>
            This will delete only your own friend requests, both sent and received.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleResetAllRequests}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset ALL Friend Requests'}
          </Button>
          <Typography variant="caption" display="block" mt={1}>
            This will delete ALL friend requests in the system for ALL users. Use with caution!
          </Typography>
        </Box>

        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {result.message}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ResetFriendRequests; 